import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FeedbackButtons } from "./FeedbackButtons";
import { Lightbulb, X, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface InsightCardProps {
  title: string;
  description: string;
  priority?: "low" | "medium" | "high";
  actionLabel?: string;
  onAction?: () => void;
  onDismiss?: () => void;
  onFeedback?: (feedback: "positive" | "negative") => void;
  className?: string;
}

export function InsightCard({
  title,
  description,
  priority = "medium",
  actionLabel,
  onAction,
  onDismiss,
  onFeedback,
  className,
}: InsightCardProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  const priorityStyles = {
    low: "border-muted",
    medium: "border-primary/20 bg-primary/5",
    high: "border-orange-500/30 bg-orange-500/5",
  };

  return (
    <Card className={cn("relative overflow-hidden", priorityStyles[priority], className)}>
      <CardContent className="p-4">
        <div className="flex gap-3">
          <div className="shrink-0 mt-0.5">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Lightbulb className="h-4 w-4 text-primary" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-medium text-sm">{title}</h4>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 -mt-1 -mr-1"
                onClick={handleDismiss}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
            <div className="flex items-center justify-between mt-3">
              {actionLabel && onAction ? (
                <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={onAction}>
                  {actionLabel}
                  <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              ) : (
                <div />
              )}
              {onFeedback && (
                <FeedbackButtons
                  onPositive={() => onFeedback("positive")}
                  onNegative={() => onFeedback("negative")}
                />
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
